/**
 * Who changed what, and when.
 *
 * The grid answers "how has this project been going"; the feed answers "what
 * exactly happened". Clicking a day in the grid filters the feed to that day,
 * which is the one interaction that makes a heatmap more than decoration.
 */
import { useMemo, useState } from 'react'
import { GitCommitVertical, X } from 'lucide-react'
import {
  byNewest, dayKeyOf, describeChange, type Change, type ProjectId, type UserId,
} from '@lattice/core'
import { useWorkspace, personOf } from '@/store/workspace'
import { useUi } from '@/store/ui'
import { ContributionGrid } from '@/components/ContributionGrid'
import { ActivityStats } from '@/components/ActivityStats'
import { FileIcon } from '@/components/FileIcon'
import { Avatar, Button, Tooltip, EmptyState, Badge } from '@/components/ui/primitives'
import { formatRelative, formatExact, formatDay, formatBytes, pluralise } from '@/lib/format'
import { cn } from '@/lib/utils'

const PAGE = 120

export function ActivityView({ projectId }: { projectId: ProjectId }) {
  const changes = useWorkspace(s => s.changes)
  const roster = useWorkspace(s => s.roster)
  const { open, go } = useUi()

  const [day, setDay] = useState<string>()
  const [author, setAuthor] = useState<UserId>()
  const [shown, setShown] = useState(PAGE)

  const mine = useMemo(() => changes.filter(c => c.projectId === projectId), [changes, projectId])

  const filtered = useMemo(() => {
    let rows = mine
    if (day) rows = rows.filter(c => dayKeyOf(c.at) === day)
    if (author) rows = rows.filter(c => c.authorId === author)
    return [...rows].sort(byNewest)
  }, [mine, day, author])

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-5xl px-4 py-4">
        <ContributionGrid
          changes={mine}
          roster={roster}
          authorId={author}
          // Passing the key at all makes the grid's highlight ours to own, which
          // is the only way "Clear filter" below can also clear the ring.
          selectedDay={day}
          onSelectDay={selected => { setDay(selected === day ? undefined : selected); setShown(PAGE) }}
        />

        <div className="mt-6">
          <ActivityStats
            changes={mine}
            roster={roster}
            selectedAuthor={author}
            onSelectAuthor={(id: UserId) => { setAuthor(id === author ? undefined : id); setShown(PAGE) }}
          />
        </div>

        <section className="mt-6">
          <header className="sticky top-0 z-10 -mx-4 flex h-9 items-center gap-2 bg-app px-4">
            <GitCommitVertical className="h-3.5 w-3.5 text-[var(--fg-subtle)]" strokeWidth={1.75} />
            <h2 className="text-sm font-medium">
              {day ? formatDay(`${day}T12:00:00Z`) : 'All activity'}
            </h2>
            <span className="tabular text-xs text-[var(--fg-subtle)]">
              {pluralise(filtered.length, 'change')}
            </span>
            {(day || author) && (
              <Button size="sm" variant="ghost" onClick={() => { setDay(undefined); setAuthor(undefined) }}>
                <X className="h-3 w-3" strokeWidth={2} /> Clear filter
              </Button>
            )}
          </header>

          {filtered.length === 0 ? (
            <EmptyState title="Nothing here" hint="No changes match the current filter." />
          ) : (
            <ol className="divide-y divide-[var(--border)] border-y border-[var(--border)]">
              {filtered.slice(0, shown).map(change => (
                <FeedRow
                  key={change.id}
                  change={change}
                  name={personOf(roster, change.authorId).name}
                  onOpen={() => { go({ projectId, view: 'files' }); open(change.path) }}
                  avatar={<Avatar user={personOf(roster, change.authorId)} size={20} />}
                />
              ))}
            </ol>
          )}

          {filtered.length > shown && (
            <div className="py-3 text-center">
              <Button variant="secondary" onClick={() => setShown(n => n + PAGE)}>
                Show {Math.min(PAGE, filtered.length - shown)} more
              </Button>
            </div>
          )}
        </section>
      </div>
    </div>
  )
}

function FeedRow({ change, name, avatar, onOpen }: {
  change: Change
  name: string
  avatar: React.ReactNode
  onOpen: () => void
}) {
  return (
    <li>
      <button
        onClick={onOpen}
        className="snap flex w-full items-start gap-2.5 px-2 py-2 text-left hover:bg-[var(--bg-hover)]"
      >
        {avatar}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-1.5 text-sm">
            <span className="font-medium">{name}</span>
            <span className={cn('kind-' + change.kind)}>{change.kind}</span>
            <span className="inline-flex min-w-0 items-center gap-1">
              <FileIcon path={change.path} />
              <span className="truncate">{change.path.split('/').pop()}</span>
            </span>
            <Tooltip label={formatExact(change.at)}>
              <span className="text-xs text-[var(--fg-subtle)]">{formatRelative(change.at)}</span>
            </Tooltip>
          </div>
          {change.message && (
            <p className="mt-0.5 line-clamp-2 text-sm leading-snug text-[var(--fg-muted)]">{change.message}</p>
          )}
          <p className="mt-0.5 truncate text-[11px] text-[var(--fg-subtle)]">
            {change.path}
            {change.size !== undefined && <span className="tabular"> · {formatBytes(change.size)}</span>}
          </p>
        </div>
        {change.source === 'git-import' && <Badge>imported</Badge>}
      </button>
    </li>
  )
}

export { describeChange }
